<!-- *************************************************************************
	TEMPLATE
	************************************************************************* -->

<template>
	<BButton
		:class="lastClass"
		:pill="pill"
		:variant="buttonVariant"
		v-bind="$attrs"
		:aria-label="ariaLabel"
		role="button"
		:data-test-automation-id="getAutomationId('button')"
		:data-tracking-id="getAutomationId('button')"
		:disabled="disabled"
		@click="onClick($event)"
	>
		<FontAwesomeIcon
			v-if="icon && iconPosition === 'left'"
			:class="{ 'svg-left': iconPosition }"
			:icon="icon"
			:size="iconSize"
			aria-hidden="true"
		/>
		<span :class="mobileClass">{{ label }}</span>
		<FontAwesomeIcon
			v-if="icon && iconPosition === 'right'"
			:class="{ 'svg-right': iconPosition }"
			:icon="icon"
			:size="iconSize"
			aria-hidden="true"
		/>
	</BButton>
</template>

<!-- *************************************************************************
	SCRIPT
	************************************************************************* -->

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import IdMixin from '@/mixins/id';
import { BButton } from 'bootstrap-vue';

/**
 * This component renders a button.
 */
@Component({
	name: 'BaseButton',
	inheritAttrs: false,
	mixins: [IdMixin],
	props: {
		label: {
			type: String,
			default: ''
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		variant: {
			default: '',
			validator: prop =>
				[
					'secondary',
					'link',
					'primary',
					'standard',
					'styleHeader',
					'danger',
					'modal',
					'up-primary-auth',
					'up-secondary-auth',
					'register-primary',
					'register-secondary',
					'up-link',
					'claimsGrid',
					'contactForm',
					'changePassword-primary',
					'changePassword-secondary',
					''
				].includes(prop)
		},
		icon: {
			type: Array,
			default: null
		},
		iconPosition: {
			type: String,
			default: 'right'
		},
		automationId: {
			type: String,
			required: true
		},
		iconSize: {
			type: String,
			default: null
		},
		pill: {
			type: Boolean,
			default: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		compressMobile: {
			type: Boolean,
			default: false
		},
		last: {
			type: Boolean,
			default: false
		}
	},
	components: {
		BButton
	}
})
export default class BaseButton extends Vue {
	mobileClass = '';
	lastClass = '';

	created() {
		if (this.compressMobile) {
			this.mobileClass = 'mobile';
		}
		if (this.last) {
			this.lastClass = 'last';
		}
	}
	/**
	 * Returns the button variant
	 * @returns {String}
	 */
	get buttonVariant() {
		switch (this.variant) {
			case 'link':
				return 'link';
			case 'primary':
				return 'primary';
			case 'danger':
				return 'danger';
			case 'standard':
				return 'standard';
			case 'styleHeader':
				return 'styleHeader';
			case 'up-primary-auth':
				return 'up-primary-auth';
			case 'up-secondary-auth':
				return 'up-secondary-auth';
			case 'register-primary':
				return 'register-primary';
			case 'register-secondary':
				return 'register-secondary';
			case 'up-link':
				return 'up-link';
			case 'claimsGrid':
				return 'claimsGrid';
			case 'contactForm':
				return 'contactForm';
			case 'changePassword-primary':
				return 'changePassword-primary';
			case 'changePassword-secondary':
				return 'changePassword-secondary';
			default:
				return 'outline-secondary';
		}
	}

	/**
	 * Handle on click event.
	 */
	onClick(evt) {
		this.$emit('click', evt);
	}
}
</script>

<!-- *************************************************************************
	STYLE
	************************************************************************* -->

<style lang="scss" @scoped>
@import url('https://fonts.googleapis.com/css?family=Noto+Sans');
@import url('https://fonts.googleapis.com/css?family=Josefin+Sans|Lato:300,400&display=swap');
.mobile {
	@media all and (max-width: 500px) {
		display: none;
	}
}

.last {
	padding-right: 0 !important;
}

.btn {
	//font-family: 'Josefin Sans', sans-serif;
	// font-size: 16px;
	// font-weight: 400;
	// border-radius: 8px;
	// padding: 12px 33px;
	// margin: 0 10px 20px 0;

	&.btn-outline-secondary {
		border: 1px solid #0079ad;
		color: #0079ad;
		&:hover,
		&:focus {
			border: 1px solid #005274;
			background-color: #005274;
			color: white;
		}
	}
	&.btn-link {
		color: #284162 !important;
		background: none !important;
		border: none !important;
		padding: 0 !important;
		/*optional*/
		//font-family: arial, sans-serif;
		/*input has OS specific font-family*/
		text-decoration: underline;
		margin: 0;
		//margin: 0 10px 20px 0;
		text-align: left;

		font: normal normal bold 17px/20px Noto Sans;
		letter-spacing: 0px;
		opacity: 1;
	}
	&.btn-primary {
		background-color: #0070a3;
		border-color: #0070a3;
		&:hover,
		&:focus {
			border: 1px solid #005274;
			background-color: #005274;
			color: white;
		}
	}
	&.btn-standard {
		border-color: #000000;
		border-radius: 10px;
	}
	&.btn-styleHeader {
		text-align: left;
		text-decoration: underline !important;
		font: normal normal normal 16px/22px Noto Sans !important;
		letter-spacing: 0px !important;
		color: #284162 !important;
		background: none !important;
		border: none !important;
		padding: 0 !important;
		border-radius: 0 !important;
		margin: 0 !important;
	}
	&.btn-up-primary-auth {
		@media (max-width: 767px) {
			height: 46px;
			width: 100%;
		}
		@media (min-width: 768px) {
			height: 46px;
			width: 170px;
		}
		@media (min-width: 992px) {
			height: 46px;
			width: 216px;
		}
		top: 1169px;
		left: 639px;
		height: auto;
		width: 100%;
		text-align: center;
		padding-right: 10px;
		padding-left: 10px;
		padding-bottom: 1px;
		padding-top: 1px;
		font: normal normal normal 16px/42px Noto Sans;
		letter-spacing: 0px;
		color: #ffffff;
		background: #26374a 0% 0% no-repeat padding-box;
		border: 1px solid #26374a;
		border-radius: 6px;
		opacity: 1;
		&:hover:enabled,
		&:focus:enabled {
			color: #ffffff;
			background-color: #1c578a;
			border-color: #091c2d;
			opacity: 1;
		}
		&:hover:not(:enabled) {
			color: #ffffff;
		}
		&:disabled {
			opacity: 0.65;
		}
	}
	&.btn-up-secondary-auth {
		@media (max-width: 767px) {
			height: 46px;
			width: 100%;
		}
		@media (min-width: 768px) {
			height: 46px;
			width: 170px;
		}
		@media (min-width: 992px) {
			height: 46px;
			width: 216px;
		}
		top: 1169px;
		left: 639px;
		height: 46px;
		width: 216px;
		text-align: center;
		padding-right: 10px;
		padding-left: 10px;
		padding-bottom: 1px;
		padding-top: 1px;
		font: normal normal normal 16px/42px Noto Sans;
		letter-spacing: 0px;
		color: #335075;
		background: #eaebed 0% 0% no-repeat padding-box;
		border: 1px solid #dcdee1;
		border-radius: 6px;
		opacity: 1;
		&:hover:enabled,
		&:focus:enabled {
			color: #335075;
			background-color: #cfd1d5;
			border-color: #0bbbfc;
			opacity: 1;
		}
		&:hover:not(:enabled) {
			color: #335075;
		}
		&:disabled {
			opacity: 0.65;
		}
	}
	&.btn-up-link,
	&.btn-up-link:hover {
		@media (max-width: 1199px) {
			text-align: center;
		}
		top: 1181px;
		left: 882px;
		text-align: left;
		text-decoration: underline;
		font: normal normal normal 17px/23px Noto Sans;
		letter-spacing: 0px;
		color: #284162;
		opacity: 1;
	}
	&.btn-claimsGrid {
		color: #284162 !important;
		background: none !important;
		border: none !important;
		padding: 0 !important;
		/*optional*/
		//font-family: arial, sans-serif;
		/*input has OS specific font-family*/
		text-decoration: none;
		margin: 0;
		//margin: 0 10px 20px 0;
		text-align: left;

		font: normal normal bold 17px/20px Noto Sans;
		letter-spacing: 0px;
	}
	&.btn-changePassword-primary {
		@media (min-width: 500px) {
			height: 46px;
			width: 170px;
		}
		@media (min-width: 800px) {
			height: 46px;
			width: 216px;
		}
		top: 1169px;
		left: 639px;
		height: auto;
		width: 100% !important;
		text-align: center;
		padding-right: 10px;
		padding-left: 10px;
		padding-bottom: 1px;
		padding-top: 1px;
		font: normal normal normal 16px/42px Noto Sans;
		letter-spacing: 0px;
		color: #ffffff;
		background: #26374a 0% 0% no-repeat padding-box;
		border: 1px solid #26374a;
		border-radius: 6px;
		opacity: 1;
		&:hover:enabled,
		&:focus:enabled {
			color: #ffffff;
			background-color: #1c578a;
			border-color: #091c2d;
			opacity: 1;
		}
		&:hover:not(:enabled) {
			color: #ffffff;
		}
		&:disabled {
			opacity: 0.65;
		}
	}
	&.btn-changePassword-secondary {
		@media (min-width: 500px) {
			height: 46px;
			width: 170px;
		}
		@media (min-width: 800px) {
			height: 46px;
			width: 216px;
		}
		top: 1169px;
		left: 639px;
		height: auto;
		width: 100% !important;
		text-align: center;
		padding-right: 10px;
		padding-left: 10px;
		padding-bottom: 1px;
		padding-top: 1px;
		font: normal normal normal 16px/42px Noto Sans;
		letter-spacing: 0px;
		color: #335075;
		background: #eaebed 0% 0% no-repeat padding-box;
		border: 1px solid #dcdee1;
		border-radius: 6px;
		opacity: 1;
		&:hover:enabled,
		&:focus:enabled {
			color: #335075;
			background-color: #cfd1d5;
			border-color: #0bbbfc;
			opacity: 1;
		}
		&:hover:not(:enabled) {
			color: #335075;
		}
		&:disabled {
			opacity: 0.65;
		}
	}
	&.btn-register-primary {
		@media (max-width: 767px) {
			height: 46px;
			width: 100%;
		}
		@media (min-width: 768px) and (max-width: 800px) {
			height: 46px;
			width: 170px;
		}
		@media (min-width: 801px) {
			height: 46px;
			width: 216px;
		}
		top: 1169px;
		left: 639px;
		height: auto;
		//width: 100% !important;
		text-align: center;
		padding-right: 10px;
		padding-left: 10px;
		padding-bottom: 1px;
		padding-top: 1px;
		font: normal normal normal 16px/42px Noto Sans;
		letter-spacing: 0px;
		color: #ffffff;
		background: #26374a 0% 0% no-repeat padding-box;
		border: 1px solid #26374a;
		border-radius: 6px;
		opacity: 1;
		&:hover:enabled,
		&:focus:enabled {
			color: #ffffff;
			background-color: #1c578a;
			border-color: #091c2d;
			opacity: 1;
		}
		&:hover:not(:enabled) {
			color: #ffffff;
		}
		&:disabled {
			opacity: 0.65;
		}
	}
	&.btn-register-secondary {
		@media (max-width: 767px) {
			height: 46px;
			width: 100%;
		}
		@media (min-width: 768px) and (max-width: 800px) {
			height: 46px;
			width: 170px;
		}
		@media (min-width: 801px) {
			height: 46px;
			width: 216px;
		}
		top: 1169px;
		left: 639px;
		height: auto;
		//width: 100% !important;
		text-align: center;
		padding-right: 10px;
		padding-left: 10px;
		padding-bottom: 1px;
		padding-top: 1px;
		font: normal normal normal 16px/42px Noto Sans;
		letter-spacing: 0px;
		color: #335075;
		background: #eaebed 0% 0% no-repeat padding-box;
		border: 1px solid #dcdee1;
		border-radius: 6px;
		opacity: 1;
		&:hover:enabled,
		&:focus:enabled {
			color: #335075;
			background-color: #cfd1d5;
			border-color: #0bbbfc;
			opacity: 1;
		}
		&:hover:not(:enabled) {
			color: #335075;
		}
		&:disabled {
			opacity: 0.65;
		}
	}
}
</style>
